import React, { useEffect, useRef, useState } from 'react';
import type { OjtSession } from '../../../types';
import { DataService } from '../../../services/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getApp } from 'firebase/app';
import { QRCodeSVG } from 'qrcode.react';
import { AlertCircle, Camera, CheckCircle, KeyRound, PenLine, QrCode } from 'lucide-react';

/**
 * Shared sign-off primitives used by Handshake, SessionSignOff and ModeChangeSignOff.
 *
 * Each primitive is a self-contained component that takes an `onComplete` callback (with
 * an optional payload — signature data URL, etc.) and renders the appropriate input UI.
 */

// ---------- Signature Pad ----------

interface SignaturePadProps {
  /** Title shown above the pad */
  title?: string;
  /** Sub-text shown above the pad */
  hint?: string;
  /** Submit button label */
  submitLabel?: string;
  /** Called with the data URL of the signature when submitted */
  onComplete: (signatureDataUrl: string) => void;
  /** When true, replace the submit button with a compact bar layout (used in modals) */
  compact?: boolean;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  title,
  hint,
  submitLabel = 'Confirm',
  onComplete,
  compact = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: ((e as React.MouseEvent).clientX - rect.left) * scaleX,
      y: ((e as React.MouseEvent).clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPos.current = getPos(e);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    if (!pos || !lastPos.current) return;
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#0c4a6e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPos.current = pos;
    setHasSigned(true);
  };

  const endDraw = () => {
    setIsDrawing(false);
    lastPos.current = null;
  };

  const clearSignature = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setHasSigned(false);
    }
  };

  const submit = () => {
    const sig = canvasRef.current?.toDataURL('image/png') || '';
    if (sig) onComplete(sig);
  };

  const padHeight = compact ? 100 : 150;

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-aero-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-aero-700 flex items-center gap-1.5">
            <PenLine className="w-4 h-4" />
            {title || 'Signature'}
          </p>
          {hasSigned && (
            <button onClick={clearSignature} className="text-xs text-aero-400 hover:text-red-500 transition-colors">
              Clear
            </button>
          )}
        </div>
        <canvas
          ref={canvasRef}
          width={600}
          height={padHeight}
          style={{ height: padHeight }}
          className="w-full border-2 border-dashed border-aero-200 rounded-xl bg-aero-50 touch-none cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasSigned && (
          <p className="text-xs text-center text-aero-400 mt-2">{hint || 'Sign above'}</p>
        )}
      </div>

      <button
        onClick={submit}
        disabled={!hasSigned}
        className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 disabled:text-aero-400 text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2"
      >
        <CheckCircle className="w-4 h-4" />
        {submitLabel}
      </button>
    </div>
  );
};

// ---------- PIN Entry ----------

interface PinPadProps {
  /** The PIN that the entered value must match */
  expectedPin: string;
  /** Called when the entered PIN matches */
  onComplete: () => void;
  title?: string;
  hint?: string;
  submitLabel?: string;
}

export const PinPad: React.FC<PinPadProps> = ({ expectedPin, onComplete, title, hint, submitLabel = 'Verify' }) => {
  const [entered, setEntered] = useState('');
  const [error, setError] = useState('');

  const verify = () => {
    if (entered === expectedPin) {
      onComplete();
    } else {
      setError('Incorrect PIN. Try again.');
      setEntered('');
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-aero-200 p-5 text-center">
        <div className="w-12 h-12 bg-aero-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <KeyRound className="w-6 h-6 text-aero-600" />
        </div>
        <h2 className="text-lg font-bold text-aero-900 mb-1">{title || 'Enter PIN'}</h2>
        {hint && <p className="text-sm text-aero-500">{hint}</p>}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-aero-200 p-4">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          value={entered}
          onChange={(e) => { setEntered(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(''); }}
          placeholder="••••"
          className="w-full text-center text-3xl font-mono font-bold tracking-widest border border-aero-200 rounded-lg py-4 focus:outline-none focus:ring-2 focus:ring-aero-500"
          maxLength={4}
        />
      </div>

      <button
        onClick={verify}
        disabled={entered.length !== 4}
        className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 disabled:text-aero-400 text-white rounded-xl font-semibold text-sm transition-colors"
      >
        {submitLabel}
      </button>
    </div>
  );
};

// ---------- Selfie Capture (with optional Gemini two-heads verification) ----------

interface SelfieCaptureProps {
  session: OjtSession;
  /** Storage path suffix — e.g. "handshake_selfie.jpg" or `mode_change_${ts}.jpg` */
  storagePathSuffix: string;
  /** Whether to call the Gemini two-heads verification function */
  verifyTwoHeads?: boolean;
  onComplete: (imageUrl: string) => void;
  title?: string;
  hint?: string;
}

export const SelfieCapture: React.FC<SelfieCaptureProps> = ({
  session,
  storagePathSuffix,
  verifyTwoHeads = false,
  onComplete,
  title,
  hint,
}) => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraActive(true);
    } catch (e) {
      setError('Camera access denied.');
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    setCameraActive(false);
  };

  useEffect(() => () => stopCamera(), []);

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const submit = async () => {
    if (!capturedImage) return;
    setIsVerifying(true);
    setError('');
    try {
      const storage = getStorage(getApp());
      const blob = await (await fetch(capturedImage)).blob();
      const storageRef = ref(storage, `soloready/ojt/${session.id}/${storagePathSuffix}`);
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
      const imageUrl = await getDownloadURL(storageRef);

      if (verifyTwoHeads) {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp: getApp2 } = await import('firebase/app');
        const functions = getFunctions(getApp2(), 'europe-west2');
        const verifyOjtSelfie = httpsCallable(functions, 'verifyOjtSelfie');
        const result: any = await verifyOjtSelfie({ imageUrl });
        if (result.data?.twoHeadsVisible !== true) {
          // Remove the uploaded blob so repeated failures don't accumulate orphans
          try { await deleteObject(storageRef); } catch (delErr) { console.warn('Failed to delete failed-verification selfie', delErr); }
          setError('Two faces not detected. Try again.');
          setCapturedImage(null);
          return;
        }
      }
      onComplete(imageUrl);
    } catch (e) {
      console.error('Selfie capture failed', e);
      setError('Verification failed. Try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-aero-200 p-5 text-center">
        <div className="w-12 h-12 bg-aero-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Camera className="w-6 h-6 text-aero-600" />
        </div>
        <h2 className="text-lg font-bold text-aero-900 mb-1">{title || 'Take a selfie'}</h2>
        {hint && <p className="text-sm text-aero-500">{hint}</p>}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!capturedImage && !cameraActive && (
        <button onClick={startCamera} className="w-full py-3.5 bg-aero-600 hover:bg-aero-700 text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2">
          <Camera className="w-4 h-4" />
          Open camera
        </button>
      )}

      {cameraActive && (
        <div className="space-y-3">
          <div className="rounded-xl overflow-hidden border border-aero-200 bg-black">
            <video ref={videoRef} className="w-full" playsInline muted />
          </div>
          <button onClick={takePhoto} className="w-full py-3.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2">
            <Camera className="w-4 h-4" />
            Capture
          </button>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}

      {capturedImage && (
        <div className="space-y-3">
          <div className="rounded-xl overflow-hidden border border-aero-200">
            <img src={capturedImage} alt="Captured selfie" className="w-full" />
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setCapturedImage(null); startCamera(); }} className="flex-1 py-3 border border-aero-200 text-aero-600 rounded-xl font-semibold text-sm hover:bg-aero-50 transition-colors">
              Retake
            </button>
            <button
              onClick={submit}
              disabled={isVerifying}
              className="flex-1 py-3 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 text-white rounded-xl font-semibold text-sm transition-colors"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Verifying...
                </span>
              ) : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- QR Show & Tap ----------

interface QrSignOffProps {
  /** Unique QR payload — e.g. `soloready-ojt-mode-change:${session.id}:${ts}` */
  qrValue: string;
  /** Returns an unsubscribe function. The component calls onComplete when the listener fires. */
  subscribeToConfirmation: (onConfirmed: () => void) => () => void;
  /** Optional callback invoked once the QR has been verified (listener fired). */
  onComplete?: () => void;
  title?: string;
  hint?: string;
}

export const QrSignOff: React.FC<QrSignOffProps> = ({ qrValue, subscribeToConfirmation, onComplete, title, hint }) => {
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const unsub = subscribeToConfirmation(() => setVerified(true));
    return unsub;
  }, [subscribeToConfirmation]);

  // Notify parent once verification flips to true (fires exactly once per mount).
  useEffect(() => {
    if (verified && onComplete) onComplete();
  }, [verified, onComplete]);

  if (verified) {
    return (
      <div className="p-4 max-w-md mx-auto flex flex-col items-center justify-center h-48">
        <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
        <p className="text-lg font-bold text-aero-900">Verified</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-aero-200 p-5 text-center">
        <div className="w-12 h-12 bg-aero-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <QrCode className="w-6 h-6 text-aero-600" />
        </div>
        <h2 className="text-lg font-bold text-aero-900 mb-1">{title || 'Show this QR code'}</h2>
        {hint && <p className="text-sm text-aero-500">{hint}</p>}
      </div>

      <div className="bg-white rounded-xl border border-aero-200 p-6 flex flex-col items-center">
        <QRCodeSVG value={qrValue} size={192} level="M" />
        <div className="flex items-center gap-2 mt-3">
          <span className="w-2 h-2 bg-aero-400 rounded-full animate-pulse" />
          <span className="text-xs text-aero-500">Waiting for scan…</span>
        </div>
      </div>
    </div>
  );
};

/** Keys of OjtSession whose value is a boolean flag — used to type-restrict subscribeToSessionFlag. */
type BooleanSessionFlag = {
  [K in keyof OjtSession]-?: OjtSession[K] extends boolean | undefined ? K : never
}[keyof OjtSession];

/** Helper: subscribe to a single boolean field flipping true on the OJT session document. */
export const subscribeToSessionFlag = (sessionId: string, flagName: BooleanSessionFlag) => {
  return (onConfirmed: () => void) =>
    DataService.onOjtSessionChange(sessionId, (s) => {
      if ((s as OjtSession)[flagName] === true) onConfirmed();
    });
};
